import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import { INTEGRATIONS_SIMULATED } from "../config/runtime";
import { clinicalApi, type CurrentUser } from "../lib/api/clinical";
import { CathetersPanel } from "./workbench/CathetersPanel";
import { Dashboard } from "./workbench/Dashboard";
import { PatientDrawer } from "./workbench/PatientDrawer";
import { PdcaPanel } from "./workbench/PdcaPanel";
import { PredictPanel } from "./workbench/PredictPanel";
import { ReviewPanel } from "./workbench/ReviewPanel";
import { ReportsPanel } from "./workbench/ReportsPanel";
import { SettingsPanel } from "./workbench/SettingsPanel";

type Tab =
  | "dashboard"
  | "predict"
  | "catheters"
  | "reviews"
  | "pdca"
  | "reports"
  | "settings";

const TABS: { id: Tab; label: string }[] = [
  { id: "dashboard", label: "今日工作台" },
  { id: "predict", label: "风险预测" },
  { id: "catheters", label: "导管评估" },
  { id: "reviews", label: "院感复核" },
  { id: "pdca", label: "PDCA" },
  { id: "reports", label: "报表导出" },
  { id: "settings", label: "账号设置" },
];

export function ProductionApp() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<Tab>("dashboard");
  const [refreshKey, setRefreshKey] = useState(0);
  const [openPatient, setOpenPatient] = useState<string | null>(null);

  const bumpRefresh = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  useEffect(() => {
    clinicalApi
      .me()
      .then(setUser)
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  async function login(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const data = new FormData(event.currentTarget);
    const username = data.get("username");
    const password = data.get("password");
    if (typeof username !== "string" || typeof password !== "string") {
      setError("登录表单格式错误。");
      return;
    }
    try {
      const current = await clinicalApi.login(username, password);
      setUser(current);
    } catch {
      setError("登录失败，请检查账号状态或联系系统管理员。");
    }
  }

  async function logout() {
    try {
      await clinicalApi.logout();
    } finally {
      setUser(null);
    }
  }

  if (loading) return <main className="production-shell">正在验证安全会话…</main>;

  if (!user) {
    return (
      <main className="production-login">
        <form
          onSubmit={(event) => {
            void login(event);
          }}
          aria-label="CAUTI 生产系统登录"
        >
          <h1>CAUTI 智能预警与闭环防控系统</h1>
          <p>本系统仅用于风险预警与院感管理，不替代临床诊断。</p>
          <label>
            工号
            <input name="username" autoComplete="username" required maxLength={128} />
          </label>
          <label>
            密码
            <input name="password" type="password" autoComplete="current-password" required />
          </label>
          {error ? <p role="alert">{error}</p> : null}
          <button type="submit">安全登录</button>
        </form>
      </main>
    );
  }

  return (
    <main className="production-shell">
      {INTEGRATIONS_SIMULATED ? (
        <aside className="runtime-banner" role="status">
          外部医院接口当前为受控模拟模式；数据来自导入/录入，接入 HIS 后自动切换
        </aside>
      ) : null}
      <header className="app-header">
        <div>
          <h1>CAUTI 院感工作台</h1>
          <p>数据由 PostgreSQL 服务端统一管理；页面不保存患者数据或登录令牌。</p>
        </div>
        <div className="header-actions">
          <span>
            {user.display_name}（{user.role}）
          </span>
          <button type="button" onClick={bumpRefresh}>
            刷新
          </button>
          <button
            type="button"
            onClick={() => {
              void logout();
            }}
          >
            退出
          </button>
        </div>
      </header>

      <nav className="app-tabs">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? "active" : ""}
            onClick={() => {
              setTab(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === "dashboard" ? (
        <Dashboard refreshKey={refreshKey} onOpenPatient={setOpenPatient} />
      ) : null}
      {tab === "predict" ? <PredictPanel onChanged={bumpRefresh} /> : null}
      {tab === "catheters" ? <CathetersPanel user={user} /> : null}
      {tab === "reviews" ? <ReviewPanel user={user} /> : null}
      {tab === "pdca" ? <PdcaPanel user={user} /> : null}
      {tab === "reports" ? <ReportsPanel /> : null}
      {tab === "settings" ? (
        <SettingsPanel
          currentUser={user}
          onPasswordChanged={() => {
            setUser(null);
          }}
        />
      ) : null}

      {openPatient ? (
        <PatientDrawer
          patientId={openPatient}
          user={user}
          onClose={() => {
            setOpenPatient(null);
          }}
          onChanged={bumpRefresh}
        />
      ) : null}
    </main>
  );
}
